import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useP2PNavigation } from "../context/P2PNavigationContext";
import {
  buildBuyerDashboardNavContext,
  getVisibleBuyerDashboardModules,
  getVisibleBuyerDashboardSubTabs,
  isSubTabActive,
  resolveActiveBuyerDashboardModule,
} from "../config/buyerDashboardModules";
import { buildBuyerTabNotificationsPath } from "../config/buyerDashboardNotifications";
import DashboardModuleTabBadge from "./DashboardModuleTabBadge";
import { useDashboardTabNotificationCounts } from "../hooks/useDashboardTabNotificationCounts";
import buyerDashboardService from "../services/buyerDashboard.service";
import "../assets/styles/buyer_dashboard_module_nav.scss";

export function useBuyerDashboardNav() {
  const { ctx: p2pNav, loading: p2pLoading } = useP2PNavigation();
  const [navEpoch, setNavEpoch] = useState(0);

  useEffect(() => {
    const bump = () => setNavEpoch((n) => n + 1);
    window.addEventListener("storage", bump);
    return () => window.removeEventListener("storage", bump);
  }, []);

  const navCtx = useMemo(
    () => buildBuyerDashboardNavContext(p2pNav, { p2pLoading }),
    [p2pNav, p2pLoading, navEpoch],
  );
  const modules = useMemo(() => getVisibleBuyerDashboardModules(navCtx), [navCtx]);
  const moduleKeys = useMemo(() => new Set(modules.map((m) => m.key)), [modules]);

  return { navCtx, modules, moduleKeys, p2pLoading };
}

export default function BuyerDashboardModuleNav({
  showSubTabs = true,
  showModuleTabs = true,
  /** When set, show sub-tabs for this module only (use on feature pages under a module). */
  moduleKey: forcedModuleKey,
  className = "",
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { navCtx, modules, p2pLoading } = useBuyerDashboardNav();
  const fetchCounts = useCallback(() => buyerDashboardService.getTabNotificationCounts(), []);
  const tabCounts = useDashboardTabNotificationCounts({
    enabled: navCtx.canAccessBuyerDashboard,
    fetchCounts,
    pathname: location.pathname,
  });

  const activeModule = useMemo(() => {
    if (forcedModuleKey) {
      return modules.find((m) => m.key === forcedModuleKey) ?? resolveActiveBuyerDashboardModule(location.pathname, modules);
    }
    return resolveActiveBuyerDashboardModule(location.pathname, modules);
  }, [forcedModuleKey, location.pathname, modules]);
  const subTabs = useMemo(
    () => (showSubTabs && activeModule?.key !== "overview" ? getVisibleBuyerDashboardSubTabs(activeModule.key, navCtx) : []),
    [showSubTabs, activeModule, navCtx],
  );

  if (!navCtx.canAccessBuyerDashboard) return null;
  if (modules.length === 0) return null;

  return (
    <div className={`buyer-dashboard__module-nav ${className}`.trim()}>
      {showModuleTabs && (
      <div className="buyer-dashboard__module-tabs">
        {modules.map((tab) => {
          const isOverview = tab.key === "overview";
          const isActiveModule = activeModule?.key === tab.key;
          const onOverviewPage =
            location.pathname === "/dashboard/buyer" || location.pathname === "/dashboard/buyer/";

          const notificationCount = Number(tabCounts[tab.key]) || 0;
          const hasNotifications = notificationCount > 0;

          let stateClass = "";
          if (isOverview && onOverviewPage) {
            stateClass = " buyer-dashboard__module-tab--active";
          } else if (isActiveModule && !isOverview) {
            stateClass = " buyer-dashboard__module-tab--active-outline";
          } else if (isActiveModule && isOverview) {
            stateClass = " buyer-dashboard__module-tab--active";
          }
          if (hasNotifications) {
            stateClass += " buyer-dashboard__module-tab--has-notifications";
          }

          return (
            <Link
              key={tab.key}
              to={tab.to}
              className={`buyer-dashboard__module-tab${stateClass}`}
            >
              <span className="buyer-dashboard__module-tab-label">{tab.label}</span>
              <DashboardModuleTabBadge
                count={notificationCount}
                tabKey={tab.key}
                onNavigate={navigate}
                buildPath={buildBuyerTabNotificationsPath}
              />
            </Link>
          );
        })}
      </div>
      )}

      {subTabs.length > 0 && (
        <div className="buyer-dashboard__sub-tabs">
          {subTabs.map((tab) => {
            const active = isSubTabActive(location.pathname, tab);
            return (
              <Link
                key={tab.key}
                to={tab.to}
                className={`buyer-dashboard__sub-tab${active ? " buyer-dashboard__sub-tab--active" : ""}`}
              >
                {tab.label}
              </Link>
            );
          })}
        </div>
      )}

      {p2pLoading && modules.some((m) => m.key === "p2p") && (
        <div className="buyer-dashboard__module-nav-hint" aria-hidden="true">
          Loading module access…
        </div>
      )}
    </div>
  );
}
